import { Link } from 'react-router-dom';
import { useState } from 'react';
import { testimonials } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function About() {
  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 — 17c.com GitHub 资源汇总',
          description: '17c.com GitHub 资源汇总平台的品牌介绍、团队声誉与联系方式。',
          url: 'https://17c.com/about',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://17c.com' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://17c.com/about' },
            ],
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', item: 'https://17c.com' },
            { name: '关于我们', item: 'https://17c.com/about' },
          ],
        }}
      />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-6">
            关于我们
          </h1>
          <p className="text-foreground-600 mt-2 max-w-2xl">
            了解 17c.com GitHub 资源汇总平台的品牌故事与使命
          </p>
        </div>
      </div>

      <BrandSection />
      <MissionSection />
      <ReputationSection testimonials={testimonials} />
      <StatsSection />
      <ContactSection />
    </>
  );
}

function BrandSection() {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              品牌故事
            </h2>
            <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
              <p>
                17c.com GitHub 资源汇总平台诞生于开发者社区对高质量开源资源的迫切需求。我们致力于为全球华语开发者提供最全面、最实用的 GitHub 开源项目指南和资源汇总。
              </p>
              <p>
                从项目结构解析到使用教程，从开源资源整理到工具推荐，我们精心策划每一篇文章，确保内容准确、实用、易于理解。我们的团队由经验丰富的软件工程师和技术写作者组成，对开源技术充满热情。
              </p>
              <p>
                平台创立以来，已经帮助超过数万名开发者快速上手各类 GitHub 开源项目，节省了大量学习和调研的时间。我们相信，优质的开源资源应该被更多人发现和使用。
              </p>
            </div>
          </div>
          <div className="lg:w-1/2">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20clean%20tech%20office%20workspace%20with%20warm%20lighting%2C%20minimal%20desk%20setup%20with%20laptop%20and%20notebook%2C%20open%20source%20community%20collaboration%20atmosphere%2C%20soft%20cream%20and%20warm%20gray%20tones%2C%20professional%20creative%20environment%2C%20no%20people&width=800&height=500&seq=about-brand-17c-03&orientation=landscape"
              alt="17c.com GitHub 品牌故事"
              className="w-full rounded-lg"
              title="17c.com GitHub 品牌故事"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionSection() {
  const values = [
    {
      icon: 'ri-focus-3-line',
      title: '专注开源',
      desc: '深度聚焦 GitHub 开源生态，为开发者挖掘最有价值的项目和工具。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '品质保证',
      desc: '每篇文章经过严格审核，确保技术内容的准确性、时效性和实用性。',
    },
    {
      icon: 'ri-global-line',
      title: '服务华语',
      desc: '为全球华语开发者提供母语阅读的技术资源，降低学习门槛。',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: '持续更新',
      desc: '紧跟技术发展趋势，持续更新和扩充资源库，保持内容的新鲜度。',
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            我们的使命
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto">
            让每一位开发者都能轻松获取优质的 GitHub 开源资源
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v) => (
            <div key={v.title} className="text-center p-6">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className={`${v.icon} text-xl`}></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReputationSection({ testimonials }: { testimonials: typeof import('@/mocks/articles').testimonials }) {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            用户评价
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto">
            来自开发者的真实反馈
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {testimonials.map((t) => (
            <div key={t.name} className="bg-background-100 rounded-lg p-5 border border-background-200/70">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-medium text-sm">
                  {t.avatar}
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                  <div className="text-xs text-foreground-500">{t.role}</div>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
              <div className="flex gap-0.5 mt-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xs text-primary-400"></i>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[
            { value: '12+', label: '教程文章' },
            { value: '6', label: '内容分类' },
            { value: '50K+', label: '月访问量' },
            { value: '99.9%', label: '服务可用率' },
          ].map((stat) => (
            <div key={stat.label} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
              <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formUrl = 'https://readdy.ai/api/form/d9phc95i2vhsdh2d34fg';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (res.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as { message?: string })?.message
          || (parsed?.message as string)
          || (parsed?.meta as { detail?: string })?.detail
          || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接');
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="lg:w-1/2">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-8">
              如果你有任何关于 17c.com GitHub 资源汇总平台的建议、合作意向或其他问题，欢迎随时联系我们。
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-mail-line"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">电子邮箱</div>
                  <div className="text-sm text-foreground-500">contact@17c.com</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                  <i className="ri-github-fill"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">GitHub</div>
                  <a
                    href="https://github.com"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    github.com/17c
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                  <i className="ri-map-pin-line"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">办公地址</div>
                  <div className="text-sm text-foreground-500">中国 · 北京</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2">
            <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                发送消息
              </h3>
              <form onSubmit={handleSubmit} data-readdy-form="">
                <div className="hp-field">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入你的留言（最多500字）"
                    />
                    <div className="text-xs text-foreground-400 mt-1 text-right">
                      最多500字符
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 disabled:opacity-50 transition-all whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">消息已成功发送，我们会尽快回复你！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}